(() => {
  if (!customElements.get('stellr-range-selector')) customElements.define('stellr-range-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.onchange = (event) => {
        const option = event.target;
        if (!option.matches('[data-range-variant]')) return;
        this.selectVariant(option);
      };
      const selected = this.querySelector('[data-range-variant]:checked');
      if (selected) this.selectVariant(selected);
    }
    selectVariant(option) {
      const card = this.closest('.stellr-range-card');
      const input = this.querySelector('[data-variant-select]');
      const submit = this.querySelector('[data-add-to-cart]');
      input.value = option.value;
      input.dataset.available = option.dataset.available;
      submit.disabled = option.dataset.available === 'false' || submit.getAttribute('aria-busy') === 'true';
      submit.style.setProperty('--range-swatch', option.dataset.colour);
      submit.style.setProperty('--range-ink', option.dataset.ink);
      this.querySelector('[data-range-add-label]').textContent = option.dataset.available === 'false' ? this.dataset.soldOutLabel : this.dataset.addLabel;
      this.querySelector('[data-range-colour-label]').textContent = option.dataset.title;
      const price = this.querySelector('[data-range-price]');
      if (price && option.dataset.priceLabel) price.textContent = option.dataset.priceLabel;
      this.querySelector('[data-product-status]').textContent = '';
      card.querySelectorAll('[data-range-image]').forEach((image) => { image.hidden = image.dataset.rangeImage !== option.value; });
      const link = card.querySelector('.stellr-story__variant-card-link');
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('variant', option.value);
      link.href = url.href;
    }
  });
  const instances = new Set();
  let adding = false;
  let cartRead = null;
  const fetchCart = (url) => fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' })
    .then(async (response) => { if (!response.ok) throw new Error('cart'); return response.json(); });
  const readCart = (url, fresh = false) => {
    if (fresh) return fetchCart(url);
    if (!cartRead) cartRead = fetchCart(url).finally(() => { cartRead = null; });
    return cartRead;
  };
  const existingColours = (cart, ids) => new Set((cart.items || [])
    .filter((item) => item.quantity > 0 && ids.has(String(item.variant_id)))
    .map((item) => String(item.variant_id)));
  if (!customElements.get('stellr-mix-combo')) customElements.define('stellr-mix-combo', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.selected = new Set();
      this.expanded = false;
      this.toggle = this.querySelector('[data-mix-toggle]');
      this.picker = this.querySelector('[data-mix-picker]');
      this.modal = this.querySelector('[data-mix-modal]');
      if (this.modal) {
        this.modal.oncancel = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.closePicker();
        };
        this.modal.onclose = () => {
          this.expanded = false;
          this.render();
        };
      }
      this.choices = [...this.querySelectorAll('[data-mix-id]')];
      this.ids = new Set(JSON.parse(this.querySelector('[data-mix-eligible]').textContent));
      this.add = this.querySelector('[data-mix-add]');
      this.status = this.querySelector('[data-mix-status]');
      this.applyCart(JSON.parse(this.querySelector('[data-mix-cart]').textContent));
      this.onclick = (event) => {
        if (event.target.closest('[data-mix-toggle]') === this.toggle) {
          if (this.expanded) this.closePicker();
          else this.openPicker();
          return;
        }
        if (this.modal && event.target.closest('[data-mix-close]')) {
          this.closePicker();
          return;
        }
        if (this.modal && event.target === this.modal) {
          const bounds = this.modal.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
            this.closePicker();
            return;
          }
        }
        const choice = event.target.closest('[data-mix-id]');
        if (choice && this.contains(choice) && !choice.disabled && !adding) {
          const id = choice.dataset.mixId;
          if (this.selected.has(id)) this.selected.delete(id);
          else this.selected.add(id);
          this.status.textContent = '';
          this.render();
        }
        if (event.target.closest('[data-mix-add]') === this.add) this.addSelection();
      };
      instances.add(this);
    }
    disconnectedCallback() {
      if (this.modal?.open) this.modal.close();
      instances.delete(this);
      this.bound = false;
    }
    openPicker() {
      this.expanded = true;
      this.render();
      if (this.modal && !this.modal.open) this.modal.showModal();
      this.choices.find((choice) => !choice.hidden && !choice.disabled)?.focus();
    }
    closePicker(restoreFocus = true) {
      this.expanded = false;
      if (this.modal?.open) this.modal.close();
      this.render();
      if (restoreFocus && this.isConnected) this.toggle.focus();
    }
    applyCart(cart) {
      this.existing = existingColours(cart, this.ids);
      this.selected = new Set([...this.selected].filter((id) => !this.existing.has(id)));
      this.render();
    }
    render() {
      const complete = this.choices.every((choice) => this.existing.has(choice.dataset.mixId));
      if (complete) this.expanded = false;
      if (this.modal?.open && !this.expanded) this.modal.close();
      this.toggle.hidden = complete;
      this.toggle.setAttribute('aria-expanded', String(this.expanded));
      this.picker.hidden = !this.expanded;
      this.querySelector('[data-mix-instruction]').hidden = this.modal ? !complete : !this.expanded && !complete;
      this.selected = new Set([...this.selected].filter((id) => this.choices.some((choice) => choice.dataset.mixId === id && choice.dataset.available === 'true')));
      this.querySelector('[data-mix-instruction]').textContent = complete ? this.dataset.complete : this.dataset.chooseMany;
      this.querySelector('[data-mix-groups]').hidden = complete;
      this.add.hidden = complete;
      this.add.disabled = adding || this.selected.size === 0 || complete;
      this.add.setAttribute('aria-busy', String(adding));
      this.choices.forEach((button) => {
        button.hidden = this.existing.has(button.dataset.mixId);
        button.disabled = adding || button.dataset.available === 'false';
        button.setAttribute('aria-pressed', String(this.selected.has(button.dataset.mixId)));
      });
      this.querySelectorAll('fieldset').forEach((fieldset) => { fieldset.hidden = ![...fieldset.querySelectorAll('[data-mix-id]')].some((button) => !button.hidden); });
    }
    async addSelection() {
      if (adding || this.add.disabled) return;
      adding = true;
      const requested = [...this.selected];
      instances.forEach((instance) => instance.render());
      this.status.textContent = '';
      try {
        const cart = await readCart(this.dataset.cartUrl, true);
        instances.forEach((instance) => instance.applyCart(cart));
        const ids = requested.filter((id) => !this.existing.has(id));
        if (ids.length === 0) { this.status.textContent = this.dataset.added; return; }
        if (ids.some((id) => !this.choices.some((choice) => choice.dataset.mixId === id && choice.dataset.available === 'true'))) throw new Error(this.dataset.unavailable);
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })), sections: window.StellrCommerce.cartSections(), sections_url: window.location.pathname }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
        if (this.modal) this.closePicker(false);
        window.StellrCommerce.refreshCart(result, this.add, !this.closest('[data-cart-page]'));
        if (this.modal) document.querySelector('#StellrCartDrawer [data-dialog-close]')?.focus();
        const freshCart = await readCart(this.dataset.cartUrl, true);
        instances.forEach((instance) => { instance.applyCart(freshCart); instance.status.textContent = instance.dataset.added; });
      } catch (error) {
        this.status.textContent = error.message === 'cart' ? this.dataset.error : error.message;
      } finally { adding = false; instances.forEach((instance) => instance.render()); }
    }
  });
  document.addEventListener('stellr:cart-updated', async () => {
    if (adding) return;
    const first = instances.values().next().value;
    if (!first) return;
    try { const cart = await readCart(first.dataset.cartUrl); if (adding) return; instances.forEach((instance) => instance.applyCart(cart)); }
    catch { instances.forEach((instance) => { instance.status.textContent = instance.dataset.error; }); }
  });
})();
